import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { signOutCompletely } from '../services/accountSessionService';
import { userAuth } from '../services/userAuth';
import { subscriptionService } from '../services/subscriptionService';
import './SettingsDropdown.css';

interface SettingsMenuItem {
  id: string;
  label: string;
  path: string;
  section?: string;
}

export const SettingsDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { t_nested } = useLanguage();
  const isSubscribed = subscriptionService.hasActiveSubscription();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside as EventListener);
      document.addEventListener('touchstart', handleClickOutside as EventListener);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside as EventListener);
      document.removeEventListener('touchstart', handleClickOutside as EventListener);
    };
  }, [isOpen]);

  useEffect(() => { setIsOpen(false); }, [location.pathname]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const subscriptionLabel = isSubscribed
    ? (t_nested('settings.subscription') || 'Subscription')
    : (t_nested('header.upgradeToPro') || 'Upgrade');

  const menuItems: SettingsMenuItem[] = [
    { id: 'subscription', label: subscriptionLabel,                                    path: '/subscription',       section: 'account' },
    { id: 'account',      label: t_nested('settings.account') || 'Account',           path: '/settings/account',   section: 'account' },
    { id: 'language',     label: t_nested('settings.language') || 'Language',           path: '/settings/language',  section: 'account' },
    { id: 'feedback',     label: t_nested('settings.feedback') || 'Feedback',         path: '/settings/feedback',  section: 'help' },
    { id: 'privacy',      label: t_nested('settings.privacy') || 'Privacy Policy',    path: '/settings/privacy',   section: 'legal' },
    { id: 'terms',        label: t_nested('settings.terms') || 'Terms of Service',    path: '/settings/terms',     section: 'legal' },
  ];

  const handleItemClick = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const groupedItems = menuItems.reduce((acc, item) => {
    const section = item.section || 'other';
    if (!acc[section]) acc[section] = [];
    acc[section].push(item);
    return acc;
  }, {} as Record<string, SettingsMenuItem[]>);

  const sectionOrder = ['account', 'help', 'legal'];

  return (
    <div className="settings-dropdown-container" ref={dropdownRef}>
      <button
        className="settings-gear-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t_nested('settings.title') || 'Settings'}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <svg
          className="settings-gear-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div
            className="settings-dropdown-backdrop"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />

          <div className="settings-dropdown-menu">
            {/* Menu sections */}
            {sectionOrder.map((section) => {
              const items = groupedItems[section];
              if (!items || items.length === 0) return null;
              return (
                <div key={section} className="settings-dropdown-section">
                  {items.map((item) => (
                    <button
                      key={item.id}
                      className={[
                        'settings-dropdown-item',
                        isActive(item.path) ? 'active' : '',
                        item.id === 'subscription' && !isSubscribed ? 'settings-dropdown-item--upgrade' : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => handleItemClick(item.path)}
                      aria-label={item.label}
                    >
                      {item.id === 'subscription' && !isSubscribed && (
                        <span className="settings-dropdown-item__zap" aria-hidden="true">⚡</span>
                      )}
                      {item.label}
                    </button>
                  ))}
                </div>
              );
            })}
            {userAuth.isLoggedIn() && (
              <div className="settings-dropdown-section settings-dropdown-section-signout">
                <button
                  className="settings-dropdown-item settings-dropdown-item-signout"
                  onClick={() => { signOutCompletely(); setIsOpen(false); }}
                  type="button"
                >
                  {t_nested('settings.logOut')}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
